import React from "react";
import { ArrowUpRight } from "lucide-react";

const CASES = [
    {
        id: "luxe",
        industry: "Luxe & Beauté",
        client: "Maison de parfumerie européenne",
        focus: "Reprise du ROAS multi-marchés sur Meta & TikTok",
        challenge:
            "L'acquisition était saturée sur Google Ads et le ROAS plafonnait sur les marchés France, Italie et Espagne. Il fallait relancer la conversion sans diluer l'image de la marque.",
        approach: [
            "Refonte de la structure de campagne Advantage+ Shopping sur Meta.",
            "Lancement de TikTok Spark Ads sur des créatives UGC accompagnées de Spark codes.",
            "Audiences seed-based enrichies de lookalikes 1 à 3 % par marché.",
            "Tracking server-side (CAPI) pour récupérer 18 % de signal perdu côté iOS.",
        ],
        results: [
            { k: "+38%", v: "ROAS" },
            { k: "−15%", v: "CPA" },
            { k: "+22%", v: "Volume conv." },
        ],
        image: "https://images.unsplash.com/photo-1772191399367-91ed8d95664b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w8NjAzMjV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBwZXJmdW1lJTIwZGFyayUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NzgxODI3MDl8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        id: "edu",
        industry: "Formation Professionnelle",
        client: "Plateforme e-learning B2C / B2B",
        focus: "Lead generation Google Ads — qualité et volume",
        challenge:
            "CPC en hausse sur des mots-clés concurrentiels (CPF, certifications professionnelles), CTR en sous-performance et un trop fort volume de leads non qualifiés.",
        approach: [
            "Restructuration de la branche Search en Single-Theme Ad Groups (STAG).",
            "Réécriture des annonces RSA et pinning stratégique des assets clés.",
            "Smart Bidding tCPA et value-based bidding adossé au lead score CRM.",
            "Pages d'atterrissage dédiées par persona et exclusion fine des mots-clés négatifs.",
        ],
        results: [
            { k: "+42%", v: "CTR" },
            { k: "−7%", v: "CPC" },
            { k: "+31%", v: "Leads SQL" },
        ],
        image: "https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80",
    },
];

export default function CaseStudies() {
    return (
        <section
            id="etudes"
            data-testid="cases-section"
            className="relative py-28 md:py-36 border-t border-[#e8e4dd]"
        >
            <div className="max-w-[1400px] mx-auto px-5 md:px-10">
                <div className="flex items-end justify-between mb-16">
                    <div>
                        <p className="label-eyebrow mb-5">— 03 / Études de cas</p>
                        <h2
                            className="font-serif text-5xl md:text-6xl tracking-tight text-[#0e0e0c]"
                            style={{ fontWeight: 400 }}
                        >
                            Deux missions,{" "}
                            <span className="italic-serif">deux résultats.</span>
                        </h2>
                    </div>
                    <p className="hidden md:block text-sm text-[#6b6b66] max-w-xs text-right leading-relaxed">
                        Sélection — données anonymisées et représentatives
                        de missions menées au cours des 18 derniers mois.
                    </p>
                </div>

                <div className="space-y-20">
                    {CASES.map((c, idx) => (
                        <article
                            key={c.id}
                            data-testid={`case-${c.id}`}
                            className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start"
                        >
                            <div
                                className={`lg:col-span-5 ${
                                    idx % 2 === 1 ? "lg:order-2" : ""
                                }`}
                            >
                                <div className="relative aspect-[4/5] overflow-hidden">
                                    <img
                                        src={c.image}
                                        alt={c.industry}
                                        className="absolute inset-0 w-full h-full object-cover"
                                    />
                                </div>
                                <div className="mt-5 flex items-center justify-between">
                                    <p className="label-eyebrow">
                                        Étude · 0{idx + 1}
                                    </p>
                                    <p className="text-sm text-[#6b6b66] italic-serif">
                                        {c.industry}
                                    </p>
                                </div>
                            </div>

                            <div className="lg:col-span-7 lg:pl-6">
                                <p className="label-eyebrow mb-5">{c.client}</p>
                                <h3
                                    className="font-serif text-4xl md:text-5xl text-[#0e0e0c] tracking-tight leading-[1.05] mb-7"
                                    style={{ fontWeight: 400 }}
                                >
                                    {c.focus}
                                </h3>
                                <p className="text-[#2c2c2a] leading-[1.75] text-lg max-w-xl">
                                    {c.challenge}
                                </p>

                                <div className="mt-9">
                                    <p className="label-eyebrow mb-4">
                                        L'approche
                                    </p>
                                    <ol className="space-y-3">
                                        {c.approach.map((a, i) => (
                                            <li
                                                key={i}
                                                className="flex gap-5 text-[#2c2c2a] text-[15px] leading-relaxed"
                                            >
                                                <span className="font-serif text-2xl text-[#9a9890] leading-none w-6 shrink-0">
                                                    {String(i + 1).padStart(
                                                        2,
                                                        "0"
                                                    )}
                                                </span>
                                                <span>{a}</span>
                                            </li>
                                        ))}
                                    </ol>
                                </div>

                                <div className="mt-10 pt-8 border-t border-[#e8e4dd] grid grid-cols-3 gap-6">
                                    {c.results.map((r, i) => (
                                        <div key={i}>
                                            <div
                                                className="font-serif text-4xl md:text-5xl text-[#0e0e0c]"
                                                style={{
                                                    fontWeight: 400,
                                                    letterSpacing: "-0.02em",
                                                }}
                                            >
                                                {r.k}
                                            </div>
                                            <div className="label-eyebrow mt-2">
                                                {r.v}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <a
                                    href="mailto:quentinsoulton@gmail.com"
                                    data-testid={`case-cta-${c.id}`}
                                    className="mt-10 link-quiet inline-flex items-center gap-2 text-[14px] tracking-wide"
                                >
                                    Discuter d'un projet similaire
                                    <ArrowUpRight size={14} />
                                </a>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}
